import { createContext, useContext, useEffect, useState } from "react";
import api from "./api";

const ThemeCtx = createContext(null);

const DEFAULTS = {
  platform_name: "Acquilix",
  tagline: "The operating system for growing businesses — project management, HRMS, CRM and ops in one secure workspace.",
  primary_hsl: "199 89% 48%",
  ring_hsl: "199 89% 48%",
  background_hsl: "0 0% 98%",
  foreground_hsl: "0 0% 9%",
  accent_hsl: "199 89% 96%",
  destructive_hsl: "0 84% 60%",
  radius: "0.25rem",
  font_heading: "Cabinet Grotesk",
  font_body: "General Sans",
  font_scale: 1.0,
  heading_tracking: "tight",
  heading_weight: 700,
  body_weight: 400,
  label_tracking: "widest",
  label_uppercase: true,
  label_weight: 600,
  section_heading_weight: 700,
  nav_label_weight: 500,
  logo_url: null,
  logo_icon_url: null,
};

function trackingPx(t) {
  return ({ tighter: "-0.05em", tight: "-0.025em", normal: "0", wide: "0.025em", wider: "0.05em", widest: "0.1em" })[t] || "0";
}

// Iter Feb 2026 R34 — Defensive helpers so a malformed branding payload
// (e.g. a stray object or non-string logo URL from a bad upload response)
// can NEVER throw during token application and white-screen the tree.
const _str = (v) => (typeof v === "string" && v.trim() ? v : "");
function _safeSet(root, name, value) {
  try {
    const s = _str(value);
    if (s) root.style.setProperty(name, s);
  } catch { /* setProperty never throws in practice — guard just in case. */ }
}

function applyTokens(b) {
  if (!b || typeof b !== "object") return;
  const root = document.documentElement;
  try {
    if (b.primary_hsl) {
      _safeSet(root, "--primary", b.primary_hsl);
      _safeSet(root, "--ring", b.ring_hsl || b.primary_hsl);
    }
    _safeSet(root, "--background", b.background_hsl);
    _safeSet(root, "--foreground", b.foreground_hsl);
    _safeSet(root, "--accent", b.accent_hsl);
    _safeSet(root, "--destructive", b.destructive_hsl);
    _safeSet(root, "--radius", b.radius);
    if (_str(b.font_heading)) root.style.setProperty("--font-heading", `'${_str(b.font_heading)}', sans-serif`);
    if (_str(b.font_body)) root.style.setProperty("--font-sans", `'${_str(b.font_body)}', sans-serif`);
    if (b.font_scale) root.style.setProperty("--font-scale", String(b.font_scale));
    // Typography knobs surfaced as custom props consumed by base styles.
    root.style.setProperty("--heading-tracking", trackingPx(b.heading_tracking));
    root.style.setProperty("--heading-weight", String(b.heading_weight || 700));
    root.style.setProperty("--section-heading-weight", String(b.section_heading_weight || 700));
    root.style.setProperty("--body-weight", String(b.body_weight || 400));
    root.style.setProperty("--label-tracking", trackingPx(b.label_tracking));
    root.style.setProperty("--label-weight", String(b.label_weight || 600));
    root.style.setProperty("--label-transform", b.label_uppercase === false ? "none" : "uppercase");
    root.style.setProperty("--nav-label-weight", String(b.nav_label_weight || 500));
    // Favicon swap — uses square icon if provided, else wide logo.
    const ico = _str(b.logo_icon_url) || _str(b.logo_url);
    if (ico) {
      let link = document.querySelector("link[rel='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = ico;
    }
    // Iter 106 — Do NOT clobber the <title> on public SEO pages (KB browse etc.);
    // those pages set their own document.title for search-engine indexing.
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    if (!path.startsWith("/help/")) {
      document.title = _str(b.platform_name) || "Acquilix";
    }
  } catch (e) {
    // Never let branding application crash the app boot.
    // eslint-disable-next-line no-console
    console.warn("[theme] applyTokens skipped due to error:", e);
  }
}

// Iter Feb 2026 R34 — Coerce the incoming branding payload so every field
// we render / apply is either the expected primitive or an empty string.
// This is the last line of defence against a malformed upload response.
function _sanitizeBranding(b) {
  if (!b || typeof b !== "object") return {};
  const out = { ...b };
  const strKeys = [
    "platform_name", "tagline", "logo_url", "logo_icon_url",
    "primary_hsl", "ring_hsl", "background_hsl", "foreground_hsl",
    "accent_hsl", "destructive_hsl", "radius",
    "font_heading", "font_body",
    "heading_tracking", "label_tracking",
  ];
  strKeys.forEach((k) => {
    const v = out[k];
    if (v == null) return;
    if (typeof v === "string") return;
    // Non-string leaked in (e.g. object/number/bool). Drop it.
    out[k] = "";
  });
  return out;
}

export function ThemeProvider({ children }) {
  // R268ttt · Feb 2026 · Hydrate initial branding from the localStorage
  // cache set by the previous refresh() so returning visitors see the
  // right logo + platform name on the *first* paint (avoids a 1-2 s
  // default-Acquilix flash before /branding resolves). Falls back to
  // DEFAULTS on first visit or if the cache is corrupted.
  const _initialBranding = (() => {
    try {
      const raw = localStorage.getItem("acq-branding-cache");
      if (!raw) return DEFAULTS;
      const c = JSON.parse(raw) || {};
      return {
        ...DEFAULTS,
        ...(c.platform_name ? { platform_name: c.platform_name } : {}),
        ...(c.logo_url ? { logo_url: c.logo_url } : {}),
        ...(c.logo_icon_url ? { logo_icon_url: c.logo_icon_url } : {}),
      };
    } catch { return DEFAULTS; }
  })();
  const [branding, setBranding] = useState(_initialBranding);
  // R118 · High-contrast toggle (WCAG-AAA). Persisted per browser via
  // localStorage. Applied by flipping `data-hc="1"` on <html>, which
  // the CSS-variable override block in index.css consumes.
  const [highContrast, setHighContrast] = useState(() => {
    try { return localStorage.getItem("acq-hc") === "1"; } catch { return false; }
  });
  useEffect(() => {
    document.documentElement.setAttribute("data-hc", highContrast ? "1" : "0");
    try { localStorage.setItem("acq-hc", highContrast ? "1" : "0"); } catch { /* quota */ }
  }, [highContrast]);

  const refresh = async () => {
    try {
      const { data } = await api.get("/branding");
      const clean = _sanitizeBranding(data);
      const merged = { ...DEFAULTS, ...clean };
      setBranding(merged);
      applyTokens(merged);
      // r-theme-fouc-fix · cache the tokens the inline script in
      // index.html reads on next boot so returning visitors don't see
      // the default-orange → workspace-brand flash.
      try {
        localStorage.setItem("acq-branding-cache", JSON.stringify({
          primary_hsl: merged.primary_hsl,
          ring_hsl: merged.ring_hsl,
          background_hsl: merged.background_hsl,
          foreground_hsl: merged.foreground_hsl,
          accent_hsl: merged.accent_hsl,
          destructive_hsl: merged.destructive_hsl,
          radius: merged.radius,
          // R268ttt · Feb 2026 · Also cache the workspace's logo URLs
          // + platform name so the marketing nav / SPA header show
          // the *correct* branding on the very first paint for
          // returning visitors (no default-Acquilix flash for
          // whitelabeled tenants).
          logo_url: merged.logo_url || null,
          logo_icon_url: merged.logo_icon_url || null,
          platform_name: merged.platform_name || null,
        }));
      } catch { /* quota / disabled — non-fatal */ }
    } catch {
      applyTokens(DEFAULTS);
    }
  };

  useEffect(() => { refresh(); }, []);

  return (
    <ThemeCtx.Provider value={{ branding, refresh, highContrast, setHighContrast }}>{children}</ThemeCtx.Provider>
  );
}

export const useTheme = () => useContext(ThemeCtx);
